import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react'

import { login } from '../actions/AdminAction.js'
import Loading from '../components/Loading'
import { getFirstAllowedRoute } from '../constants/menuConstants'
import { AUTH_NOTICE_KEY } from '../components/SessionExpiryWatcher'
import { Button, ByPokerLauncher, FullLogo, TextInput, ThemeToggle } from '../ui'

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const dispatch = useDispatch()

  const handleLogin = (e) => {
    e?.preventDefault?.()
    if (!email || !password) {
      toast('Enter your email and password to sign in.', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    } else {
      dispatch(login(email.toLowerCase(), password))
    }
  }

  // Why the last session ended (expired, ended from another device, ...)
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(AUTH_NOTICE_KEY)
      if (notice) {
        sessionStorage.removeItem(AUTH_NOTICE_KEY)
        toast(notice, { type: 'info', hideProgressBar: true, autoClose: 5000 })
      }
    } catch {
      // storage unavailable
    }
  }, [])

  const adminLogin = useSelector((state) => state.adminLogin)
  const { loadingUserInfo, errorUserInfo, userInfo } = adminLogin

  useEffect(() => {
    if (userInfo) {
      // Came from a club QR code: show the welcome popup on the next page
      const pendingWelcomeClub = sessionStorage.getItem('pendingWelcomeClub');
      if (pendingWelcomeClub) {
        sessionStorage.setItem('showWelcomePopup', 'true');
      }

      if (userInfo.role && userInfo.role.name === 'Client') {
        navigate('/banktasks');
      } else if (userInfo.role && userInfo.role.name === 'Collection Supervisor') {
        navigate('/dailyCollection');
      } else if (userInfo.role && userInfo.role.name === 'Cashier') {
        navigate('/admin/transactions'); // Cashiers go directly to transactions
      } else if (userInfo.role && (userInfo.role.name === 'Dealer' || userInfo.role.dealer === true)) {
        toast.success('Dealer logged in successfully')
        navigate('/dealer-management');
      } else {
        // Other roles land on the first page their permissions allow
        navigate(getFirstAllowedRoute(userInfo.role) || '/dashboard')
      }
    } else if (errorUserInfo) {
      toast(errorUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [userInfo, errorUserInfo, navigate])

  if (loadingUserInfo) {
    return <Loading fullScreen label="Signing you in…" />
  }

  return (
    <div className="min-h-screen bg-cm-bg text-cm-text grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* Brand side */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-cm-surface border-r border-cm-border p-10">
        <div
          aria-hidden="true"
          className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full opacity-60"
          style={{ background: 'radial-gradient(closest-side, var(--cm-red-soft), transparent)' }}
        />
        <div
          aria-hidden="true"
          className="absolute -left-20 bottom-10 w-72 h-72 rounded-full border-[18px] border-dashed border-cm-red opacity-10"
        />
        <div className="relative text-[11px] font-semibold uppercase tracking-[0.14em] text-cm-gold-text">Staff portal</div>
        <div className="relative flex flex-col items-center text-center gap-6">
          <FullLogo width={320} />
          <p className="text-cm-muted max-w-sm m-0">
            Run the floor, track every rupee and keep players moving, all from one place.
          </p>
        </div>
        <div className="relative flex justify-center">
          <ByPokerLauncher height={26} />
        </div>
      </aside>

      {/* Form side */}
      <main className="relative flex flex-col items-center justify-center px-4 py-12">
        <ThemeToggle className="absolute top-4 right-4" />
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-8">
            <FullLogo width={200} />
          </div>
          <h1 className="font-display text-[28px] font-extrabold m-0">Sign in</h1>
          <p className="text-sm text-cm-muted mt-1.5 mb-7">Use the email and password your administrator gave you.</p>

          <form className="flex flex-col gap-4" onSubmit={handleLogin} noValidate>
            <TextInput
              id="email"
              label="Email"
              type="email"
              autoComplete="username"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loadingUserInfo}
              size="lg"
            />
            <TextInput
              id="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loadingUserInfo}
              size="lg"
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="grid place-items-center w-7 h-7 -mr-1 rounded text-cm-muted hover:text-cm-text"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
            <Button type="submit" size="lg" block icon={LogIn} disabled={loadingUserInfo} className="mt-2">
              Sign In
            </Button>
          </form>

          <p className="text-xs text-cm-faint text-center mt-8 mb-0">
            Players sign in on the ChipMonk player app, not here.
          </p>
          <div className="flex justify-center mt-6 lg:hidden">
            <ByPokerLauncher height={22} />
          </div>
        </div>
      </main>
    </div>
  )
}

export default Login
